import React from 'react'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { SkillEnum, SkillTreeLevel } from '../../types'
import { calculateOverallSkillProgress, getOverallSkillLevel } from '../../utils'
import { ProgressBar } from './ProgressBar'
import { IndividualSkillCard } from './IndividualSkillCard'
import SKILLS from '../../constants/skills'

interface OverallSkillCardProps {
  skillLevel: SkillTreeLevel
  gameSkills: any
  isExpanded: boolean
  onToggle: () => void
  isTransparent?: boolean
}

export function OverallSkillCard({
  skillLevel,
  gameSkills,
  isExpanded,
  onToggle,
  isTransparent = true
}: OverallSkillCardProps) {
  const isUnlocked = !!getOverallSkillLevel([skillLevel], gameSkills)
  const overallProgress = calculateOverallSkillProgress(skillLevel.unlockRequirements, gameSkills)

  const backgroundClass = isTransparent ? 'bg-white bg-opacity-20 backdrop-blur-sm' : 'bg-blue-700'

  return (
    <div className={`${backgroundClass} rounded-lg p-4 shadow-lg`}>
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-lg font-semibold text-white">{skillLevel.name}</h4>
        <div className="flex items-center">
          {isUnlocked ? (
            <Check className="w-6 h-6 text-green-400 mr-2" />
          ) : (
            <div className="w-24">
              <ProgressBar progress={overallProgress} total={100} />
            </div>
          )}
          <span className="text-yellow-300 font-bold ml-2">Level {skillLevel.level}</span>
          <button className="ml-2 text-white hover:text-blue-200 transition-colors" onClick={onToggle}>
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <p className="text-white text-sm">{skillLevel.description}</p>
      {isExpanded && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(skillLevel.unlockRequirements).map(([skillKey, requiredLevel]) => (
            <IndividualSkillCard
              key={skillKey}
              skillKey={skillKey as SkillEnum}
              reachedLevel={gameSkills[skillKey as SkillEnum].level}
              requiredLevel={requiredLevel as number}
              gameSkill={gameSkills[skillKey as SkillEnum]}
              showDetails={false}
              isGoatLevel={skillLevel.level === 11}
            />
          ))}
        </div>
      )}
    </div>
  )
}
